import { ApprovalMode } from '@insio/shared-types';
import type { IconProps } from '@insio/ui-toolkit';

/** Documents with an automation lifecycle of their own. */
export type LifecycleDocument = 'invoice' | 'requisition' | 'purchaseOrder';

/** Icon of the creation badge that opens each lifecycle diagram. */
export const CREATION_ICON: Record<LifecycleDocument, IconProps['name']> = {
  invoice: 'cloud-arrow-up',
  requisition: 'plus',
  purchaseOrder: 'bag-shopping'
};

/** Steps of the invoice lifecycle, in the order they run. */
export type InvoiceLifecycleStepKey =
  'extraction' | 'autofill' | 'matching' | 'validation' | 'approval' | 'export' | 'closure';

/** Steps of the requisition lifecycle, in the order they run. */
export type RequisitionLifecycleStepKey = 'validation' | 'approval' | 'ordering' | 'closure';

/** Steps of the purchase order lifecycle, in the order they run. */
export type PurchaseOrderLifecycleStepKey = 'validation' | 'approval' | 'closure';

/** Each document's own step keys. */
export interface LifecycleStepKeyByDocument {
  invoice: InvoiceLifecycleStepKey;
  requisition: RequisitionLifecycleStepKey;
  purchaseOrder: PurchaseOrderLifecycleStepKey;
}

export type LifecycleStepKey = LifecycleStepKeyByDocument[LifecycleDocument];

/** One step of one document's lifecycle — what the overview opens. */
export interface LifecycleStepRef {
  document: LifecycleDocument;
  step: LifecycleStepKey;
}

/** One step of the lifecycle diagram together with its automation state. */
export interface LifecycleStep {
  key: LifecycleStepKey;
  icon: IconProps['name'];
  /** No settings of its own — a lock replaces the status dot. */
  isLocked: boolean;
  /** State line under the title. */
  stateLabel: string;
  /** Whether the automation runs; ignored on locked steps. */
  isEnabled?: boolean;
}

/** A switchable step that does not run — drawn dashed and grey. */
export function isStepOff(step: LifecycleStep): boolean {
  return !step.isLocked && !step.isEnabled;
}

/** Lifecycle state label key of each approval mode. */
export const APPROVAL_MODE_LABEL_KEY: Record<ApprovalMode, 'aiAndUser' | 'userOnly' | 'skipped'> = {
  [ApprovalMode.AI_AND_USER]: 'aiAndUser',
  [ApprovalMode.USER_ONLY]: 'userOnly',
  [ApprovalMode.SKIPPED]: 'skipped'
};

/** Every step of each lifecycle, in the order they run. */
export const LIFECYCLE_STEP_KEYS: {
  readonly [D in LifecycleDocument]: readonly LifecycleStepKeyByDocument[D][];
} = {
  invoice: ['extraction', 'autofill', 'matching', 'validation', 'approval', 'export', 'closure'],
  requisition: ['validation', 'approval', 'ordering', 'closure'],
  purchaseOrder: ['validation', 'approval', 'closure']
};

function isLifecycleDocument(value: string): value is LifecycleDocument {
  return Object.hasOwn(LIFECYCLE_STEP_KEYS, value);
}

/** The `<document>.<step>` URL value that opens one step (e.g. `purchaseOrder.closure`). */
export type LifecycleStepParam = {
  [D in LifecycleDocument]: `${D}.${LifecycleStepKeyByDocument[D]}`;
}[LifecycleDocument];

/** The URL value opening one step of one document's lifecycle. */
export function lifecycleStepParam<D extends LifecycleDocument>(
  document: D,
  step: LifecycleStepKeyByDocument[D]
): LifecycleStepParam {
  // A `${D}.${step}` of a generic D is not narrowed to the mapped union by the compiler.
  return `${document}.${step}` as LifecycleStepParam;
}

/** The step a `<document>.<step>` URL value names (e.g. `purchaseOrder.closure`), or null. */
export function lifecycleStepFromParam(value: string | null): LifecycleStepRef | null {
  const [document = '', step, ...rest] = (value ?? '').split('.');
  if (rest.length > 0 || !isLifecycleDocument(document)) return null;
  const keys: readonly LifecycleStepKey[] = LIFECYCLE_STEP_KEYS[document];
  const key = keys.find(candidate => candidate === step);
  return key ? { document, step: key } : null;
}

/** Whether a step belongs to P2P and is shown only with its flag. */
export function isProcurementStep({ document, step }: LifecycleStepRef): boolean {
  return document !== 'invoice' || step === 'matching';
}

/** Translation path of a step under `lifecycle.steps` — invoice steps keep their flat keys. */
export function stepLabelKey(document: LifecycleDocument, step: LifecycleStepKey): string {
  return document === 'invoice' ? step : `${document}.${step}`;
}

/** The completion settings a lifecycle step summarises. */
export interface CompletionSummary {
  completionBaseStatus: string;
  isDeliveryRequired: boolean;
  isInvoicingRequired: boolean;
}

/** Translation keys under the closure namespace that summarise when a document completes. */
export function completionSummaryKeys(settings: CompletionSummary): string[] {
  return [
    `summary.${settings.completionBaseStatus}`,
    ...(settings.isDeliveryRequired ? ['summary.delivered'] : []),
    ...(settings.isInvoicingRequired ? ['summary.invoiced'] : [])
  ];
}
